import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { graphql } from 'gatsby';
import { useQuery, useLazyQuery } from '@apollo/client';
import { Layout, SEO } from '../components';
import DocumentsList from '../components/documents/DocumentsList';
import Clouds from '../assets/images/clouds.svg';
import {
  FETCH_MORE_DOCUMENTS_QUERY,
  FETCH_FEATURED_DOCUMENTS_QUERY,
} from '../graphql/queries/documents';

const ModuleSubCategoryDocumentsPage = ({
  data,
  data: {
    subCategoryPage: { module, heading, description, seoKeywords, seo },
    datocms: {
      documents,
      documentsCount: { count: totalDocuments },
      freeDocumentsCount
    },
  },
  pageContext: { categories, id, module: moduleId },
}) => {
  const PAGE_SIZE = 100;
  const [combinedDocuments, setCombinedDocuments] = useState(
    documents.filter((doc) => !doc.isFeatured && !doc.freeSample),
  );
  const [currentPage, setCurrentPage] = useState(0);
  const freeDocuments = documents.filter((doc) => doc.freeSample && !doc.freeDocument);
  const context = {
    clientName: 'dato-cms',
  };
  const variables = {
    id,
    module: moduleId,
    categories,
    first: PAGE_SIZE,
    skip: (currentPage + 1) * PAGE_SIZE,
  };
  const { data: featuredDocumentsData, loading: featuredDocumentsLoading } = useQuery(
    FETCH_FEATURED_DOCUMENTS_QUERY,
    {
      context,
      variables,
    },
  );

 
  // const hasNextPage =
  //   combinedDocuments.length >= (currentPage + 1) * PAGE_SIZE ||
  //   combinedDocuments.length !== totalDocuments;

  const hasNextPage = totalDocuments >= (currentPage + 1) * PAGE_SIZE

  const onDocumentFetchSuccess = (data) => {
    if (data) {
      const newDocuments = [...combinedDocuments, ...data.documents];
      setCombinedDocuments(newDocuments);
    }
  };

  const [fetchMoreDocuments, { loading, networkStatus }] = useLazyQuery(
    FETCH_MORE_DOCUMENTS_QUERY,
    {
      onCompleted: onDocumentFetchSuccess,
      notifyOnNetworkStatusChange: true,
    },
  );
  const isRefetching = networkStatus === 3;

  const handleFetchMore = () => {

    fetchMoreDocuments({
      variables,
      context,
    });

    setCurrentPage(currentPage + 1);
  };

  const renderFeaturedDocuments = () => {
    if (!!featuredDocumentsLoading) return <p>Loading...</p>;

    return (
      !!featuredDocumentsData.featuredDocuments.length && (
        <div className="documents-list">
          <div className="category-breadcrumb-heading">
            <h3>Featured documents:</h3>
          </div>
          <DocumentsList documents={featuredDocumentsData.featuredDocuments} />
        </div>
      )
    );
  };

  const renderFreeDocuments = () =>
    freeDocuments.length > 0 && (
      <div className="documents-list">
        <div className="category-breadcrumb-heading">
          <h3>Free samples:</h3>
        </div>
        <DocumentsList documents={freeDocuments} />
      </div>
    );

  const renderDocumentsHeading = () =>
    documents[0] && (
      <div className="category-breadcrumb-heading">
        <h3 style={{ marginBottom: 15 }}>
          {documents[0].documentCategory[documents[0].documentCategory.length - 1].name}:
        </h3>
        <p>Total of {totalDocuments}</p>
      </div>
    );

  return (
    <Layout>
      <SEO
        description={seo && seo.description}
        image={seo && seo.image}
        title={seo && seo.title}
        seoKeywords={seoKeywords}
      />
      <div>
        <div className="container">
          <div className="w-layout-grid grid hero">
            <div className="content-block">
              <h1 className="content-h2">{module.title}</h1>
              <h2>{heading.name}</h2>
              <br />
              <ReactMarkdown className="paragraph" children={description} />
            </div>
            <img src={module.image.url} alt={module.image.alt} />
          </div>
        </div>
        <img src={Clouds} alt="Clouds" className="pattern-left" />
      </div>
      <article className="content-section">
        <div className="content-block container">
          {renderFeaturedDocuments()}
          {renderFreeDocuments()}
          {renderDocumentsHeading()}
          <DocumentsList documents={combinedDocuments} showNoData type={heading.name} />
          <br />
          {hasNextPage && (
            <button
              className="button-blue w-button"
              disabled={isRefetching}
              loading={String(isRefetching)}
              onClick={handleFetchMore}
              type="button"
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          )}
        </div>
      </article>
    </Layout>
  );
};

export default ModuleSubCategoryDocumentsPage;

const query = graphql`
  query SUB_CATEGORY_DOCUMENTS_PAGE_QUERY(
    $module: DATOCMS_ItemId
    $id: String!
    $categories: [DATOCMS_ItemId]
  ) {
    subCategoryPage: datoCmsModuleSubCategoryPage(id: { eq: $id }) {
      module {
        title
        image {
          alt
          url
        }
      }
      heading {
        name
      }
      description
      showSubCategories
      subCategoryList {
        subCategoryHeading {
          id
          name
          slug
        }
        subCategoryOptionsList {
          name
          slug
        }
      }
      seoKeywords {
        keyword
      }
      seo {
        description
        title
        image {
          url
        }
      }
    }
    datocms {
      documentsCount: _allDocumentsMeta(
        filter: {
          documentCategory: { allIn: $categories }
          module: { eq: $module }
          isFeatured: { eq: "false" }
          freeSample: { eq: "false" }

        }
      ) {
        count
      }

      freeDocumentsCount: _allDocumentsMeta(
        filter: {
          documentCategory: { allIn: $categories }
          module: { eq: $module }
          isFeatured: { eq: "false" }
          freeSample: { eq: "true" }

        }
      ) {
        count
      }
      
      documents: allDocuments(
        filter: {
          documentCategory: { allIn: $categories }
          module: { eq: $module }
          isFeatured: { eq: "false" }
        }
        orderBy: name_ASC
        first: "100"
      ) {
        id
        name
        module {
          slug
        }
        documentCategory {
          name
          id
          slug
        }
        price
        priceCode
        fileUrl
        freeDocument
        freeSample
        isFeatured
        hasPassword
      }

    }
  }
`;
export { query };
